import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { styled as styledMUI } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { TOAST_CONFIG } from '../../shared/Constants';
import { useERPConnections, ERPConnection } from '../../shared/context/ERPConnectionsContext';

export interface ERPConnectionModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

type TabKey = 'omie' | 'bling'; // | 'pje' — PJe tab disabled for now

// PJe integration temporarily disabled — uncomment when ready
// const UF_OPTIONS = [
//   'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
//   'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
//   'SE', 'SP', 'TO',
// ];

function ERPConnectionModal({ openModal, setOpenModal }: ERPConnectionModalProps) {
  const { connections, loading, fetchConnections, connectOmie, initiateBlingOAuth, /* connectPje, */ disconnectERP } = useERPConnections();
  const [activeTab, setActiveTab] = useState<TabKey>('omie');

  // Omie form
  const [omieAppKey, setOmieAppKey] = useState('');
  const [omieAppSecret, setOmieAppSecret] = useState('');
  const [omieConnecting, setOmieConnecting] = useState(false);

  // Bling form
  const [blingClientId, setBlingClientId] = useState('');
  const [blingClientSecret, setBlingClientSecret] = useState('');
  const [blingConnecting, setBlingConnecting] = useState(false);

  // PJe form — disabled for now
  // const [pjeOab, setPjeOab] = useState('');
  // const [pjeUf, setPjeUf] = useState('SP');
  // const [pjeConnecting, setPjeConnecting] = useState(false);

  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  useEffect(() => {
    if (openModal) {
      fetchConnections();
    }
  }, [openModal, fetchConnections]);

  const getConnection = (provider: string): ERPConnection | undefined =>
    connections.find(c => c.provider === provider);

  const handleConnectOmie = async () => {
    if (!omieAppKey.trim() || !omieAppSecret.trim()) {
      toast.error('Preencha App Key e App Secret.', TOAST_CONFIG);
      return;
    }
    setOmieConnecting(true);
    const result = await connectOmie(omieAppKey, omieAppSecret);
    setOmieConnecting(false);
    if (result.success) {
      toast.success(result.message, TOAST_CONFIG);
      setOmieAppKey('');
      setOmieAppSecret('');
    } else {
      toast.error(result.message, TOAST_CONFIG);
    }
  };

  const handleConnectBling = async () => {
    if (!blingClientId.trim() || !blingClientSecret.trim()) {
      toast.error('Preencha Client ID e Client Secret.', TOAST_CONFIG);
      return;
    }
    setBlingConnecting(true);
    const result = await initiateBlingOAuth(blingClientId, blingClientSecret);
    setBlingConnecting(false);
    if (result.success && result.authorize_url) {
      window.open(result.authorize_url, '_blank', 'width=600,height=700');
      setBlingClientId('');
      setBlingClientSecret('');
    } else {
      toast.error(result.message || 'Erro ao conectar Bling.', TOAST_CONFIG);
    }
  };

  // PJe connection handler — disabled for now
  // const handleConnectPje = async () => {
  //   if (!pjeOab.trim()) {
  //     toast.error('Preencha o número da OAB.', TOAST_CONFIG);
  //     return;
  //   }
  //   if (!pjeUf.trim()) {
  //     toast.error('Selecione o estado (UF).', TOAST_CONFIG);
  //     return;
  //   }
  //   setPjeConnecting(true);
  //   const result = await connectPje(pjeOab.trim(), pjeUf.trim());
  //   setPjeConnecting(false);
  //   if (result.success) {
  //     toast.success(result.message, TOAST_CONFIG);
  //     setPjeOab('');
  //     setPjeUf('SP');
  //   } else {
  //     toast.error(result.message, TOAST_CONFIG);
  //   }
  // };

  const handleDisconnect = async (provider: string) => {
    const labels: Record<string, string> = { omie: 'Omie', bling: 'Bling' /* , pje: 'PJe' */ };
    const confirmMsg = `Deseja desconectar o ${labels[provider] || provider}?`;
    if (!window.confirm(confirmMsg)) return;
    setDisconnecting(provider);
    const result = await disconnectERP(provider);
    setDisconnecting(null);
    if (result.success) {
      toast.success(result.message, TOAST_CONFIG);
    } else {
      toast.error(result.message, TOAST_CONFIG);
    }
  };

  const omieConnection = getConnection('omie');
  const blingConnection = getConnection('bling');
  // const pjeConnection = getConnection('pje');

  return (
    <StyledModal
      open={openModal}
      onClose={() => setOpenModal(false)}
      BackdropProps={{ onClick: (event) => event.stopPropagation() }}
    >
      <ModalContainer>
        <Header>
          <Title>Integrações ERP</Title>
          <CloseButton onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </CloseButton>
        </Header>

        <TabBar>
          <Tab $active={activeTab === 'omie'} onClick={() => setActiveTab('omie')}>
            Omie
            {omieConnection && <ConnectedDot />}
          </Tab>
          <Tab $active={activeTab === 'bling'} onClick={() => setActiveTab('bling')}>
            Bling
            {blingConnection && <ConnectedDot />}
          </Tab>
          {/* PJe tab — disabled for now
          <Tab $active={activeTab === 'pje'} onClick={() => setActiveTab('pje')}>
            PJe
            {pjeConnection && <ConnectedDot />}
          </Tab>
          */}
        </TabBar>

        <Content>
          {loading ? (
            <LoadingContainer>
              <CircularProgress size={24} />
              <span>Carregando...</span>
            </LoadingContainer>
          ) : (
            <>
              {activeTab === 'omie' && (
                <>
                  {omieConnection ? (
                    <ConnectionCard>
                      <ConnectionHeader>
                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                        <ConnectionTitle>Omie conectado</ConnectionTitle>
                      </ConnectionHeader>
                      {omieConnection.company_name && (
                        <ConnectionDetail>Empresa: {omieConnection.company_name}</ConnectionDetail>
                      )}
                      {omieConnection.connected_at && (
                        <ConnectionDetail>
                          Conectado em: {new Date(omieConnection.connected_at).toLocaleDateString('pt-BR')}
                        </ConnectionDetail>
                      )}
                      <DisconnectButton
                        onClick={() => handleDisconnect('omie')}
                        disabled={disconnecting === 'omie'}
                      >
                        {disconnecting === 'omie' ? (
                          <CircularProgress size={16} sx={{ color: '#991b1b' }} />
                        ) : (
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        )}
                        <span>Desconectar</span>
                      </DisconnectButton>
                    </ConnectionCard>
                  ) : (
                    <>
                      <Description>
                        Conecte sua conta Omie para consultar dados financeiros e de vendas diretamente no chat.
                        Você encontra as credenciais em <a href="https://developer.omie.com.br/my-apps/" target="_blank" rel="noopener noreferrer" style={{color: '#1976d2'}}>developer.omie.com.br/my-apps</a>.
                      </Description>
                      <FormGroup>
                        <Label>App Key</Label>
                        <Input
                          type="text"
                          placeholder="Sua App Key do Omie"
                          value={omieAppKey}
                          onChange={e => setOmieAppKey(e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>App Secret</Label>
                        <Input
                          type="password"
                          placeholder="Sua App Secret do Omie"
                          value={omieAppSecret}
                          onChange={e => setOmieAppSecret(e.target.value)}
                        />
                      </FormGroup>
                      <ConnectButton onClick={handleConnectOmie} disabled={omieConnecting}>
                        {omieConnecting ? (
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                          'Conectar Omie'
                        )}
                      </ConnectButton>
                    </>
                  )}
                </>
              )}

              {activeTab === 'bling' && (
                <>
                  {blingConnection ? (
                    <ConnectionCard>
                      <ConnectionHeader>
                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                        <ConnectionTitle>Bling conectado</ConnectionTitle>
                      </ConnectionHeader>
                      {blingConnection.company_name && (
                        <ConnectionDetail>Empresa: {blingConnection.company_name}</ConnectionDetail>
                      )}
                      {blingConnection.connected_at && (
                        <ConnectionDetail>
                          Conectado em: {new Date(blingConnection.connected_at).toLocaleDateString('pt-BR')}
                        </ConnectionDetail>
                      )}
                      <DisconnectButton
                        onClick={() => handleDisconnect('bling')}
                        disabled={disconnecting === 'bling'}
                      >
                        {disconnecting === 'bling' ? (
                          <CircularProgress size={16} sx={{ color: '#991b1b' }} />
                        ) : (
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        )}
                        <span>Desconectar</span>
                      </DisconnectButton>
                    </ConnectionCard>
                  ) : (
                    <>
                      <Description>
                        Conecte sua conta Bling via OAuth. Crie um aplicativo em{' '}
                        <strong>Bling &gt; Preferências &gt; Integrações &gt; API</strong> e informe o Client ID e Secret.
                      </Description>
                      <FormGroup>
                        <Label>Client ID</Label>
                        <Input
                          type="text"
                          placeholder="Client ID do aplicativo Bling"
                          value={blingClientId}
                          onChange={e => setBlingClientId(e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Client Secret</Label>
                        <Input
                          type="password"
                          placeholder="Client Secret do aplicativo Bling"
                          value={blingClientSecret}
                          onChange={e => setBlingClientSecret(e.target.value)}
                        />
                      </FormGroup>
                      <ConnectButton onClick={handleConnectBling} disabled={blingConnecting}>
                        {blingConnecting ? (
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                          'Conectar Bling'
                        )}
                      </ConnectButton>
                    </>
                  )}
                </>
              )}

              {/* PJe content — disabled for now
              {activeTab === 'pje' && (
                <>
                  {pjeConnection ? (
                    <ConnectionCard>
                      <ConnectionHeader>
                        <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                        <ConnectionTitle>PJe conectado</ConnectionTitle>
                      </ConnectionHeader>
                      {pjeConnection.company_name && (
                        <ConnectionDetail>{pjeConnection.company_name}</ConnectionDetail>
                      )}
                      {pjeConnection.connected_at && (
                        <ConnectionDetail>
                          Conectado em: {new Date(pjeConnection.connected_at).toLocaleDateString('pt-BR')}
                        </ConnectionDetail>
                      )}
                      <DisconnectButton
                        onClick={() => handleDisconnect('pje')}
                        disabled={disconnecting === 'pje'}
                      >
                        {disconnecting === 'pje' ? (
                          <CircularProgress size={16} sx={{ color: '#991b1b' }} />
                        ) : (
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        )}
                        <span>Desconectar</span>
                      </DisconnectButton>
                    </ConnectionCard>
                  ) : (
                    <>
                      <Description>
                        Conecte seu perfil de advogado para consultar processos judiciais diretamente no chat.
                        Informe seu número da OAB e estado para habilitar consultas no DataJud (base de dados do CNJ).
                      </Description>
                      <FormGroup>
                        <Label>Número da OAB</Label>
                        <Input
                          type="text"
                          placeholder="Ex: 123456"
                          value={pjeOab}
                          onChange={e => setPjeOab(e.target.value.replace(/\D/g, '').slice(0, 7))}
                          maxLength={7}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Estado (UF)</Label>
                        <Select value={pjeUf} onChange={e => setPjeUf(e.target.value)}>
                          {UF_OPTIONS.map(uf => (
                            <option key={uf} value={uf}>{uf}</option>
                          ))}
                        </Select>
                      </FormGroup>
                      <ConnectButton onClick={handleConnectPje} disabled={pjeConnecting}>
                        {pjeConnecting ? (
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                          'Conectar PJe'
                        )}
                      </ConnectButton>
                    </>
                  )}
                </>
              )}
              */}
            </>
          )}
        </Content>
      </ModalContainer>
    </StyledModal>
  );
}

export default ERPConnectionModal;

// Styled components

const StyledModal = styledMUI(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  font-family: var(--font-secondary);
  background: ${props => props.theme.modal_background};
  color: ${props => props.theme.font_color};
  border: 1px solid ${props => props.theme.modal_border};
  width: min(95vw, 520px);
  max-width: 100%;
  max-height: 90vh;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  outline: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 98vw;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 30px 15px 30px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 20px 16px 12px 16px;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${props => props.theme.font_color};
  font-family: var(--font-primary);

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.arrow_icon_color};
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.hover_overlay_light};
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${props => props.theme.modal_border};
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 0.95rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) => ($active ? theme.font_color : (theme.text_secondary || '#888'))};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#10b981' : 'transparent')};
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: var(--font-primary);

  &:hover {
    color: ${props => props.theme.font_color};
  }
`;

const ConnectedDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
`;

const Content = styled.div`
  padding: 24px 30px 30px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 20px 16px 24px 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: ${props => props.theme.text_secondary};
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.text_secondary || '#666'};
  margin: 0;
  line-height: 1.6;
  font-family: var(--font-primary);

  strong {
    color: ${props => props.theme.font_color};
    font-weight: 600;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.font_color};
  font-family: var(--font-primary);
`;

const Input = styled.input`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${props => props.theme.modal_border};
  background: ${props => props.theme.input_surface_bg || 'transparent'};
  color: ${props => props.theme.font_color};
  font-size: 0.95rem;
  font-family: var(--font-primary);
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${props => props.theme.text_secondary || '#999'};
  }

  &:focus {
    border-color: #10b981;
  }
`;

// PJe Select — disabled for now
// const Select = styled.select`
//   padding: 10px 14px;
//   border-radius: 10px;
//   border: 1px solid ${props => props.theme.modal_border};
//   background: ${props => props.theme.input_surface_bg || 'transparent'};
//   color: ${props => props.theme.font_color};
//   font-size: 0.95rem;
//   font-family: var(--font-primary);
//   outline: none;
//   transition: border-color 0.2s ease;
//   cursor: pointer;
//   &:focus {
//     border-color: #10b981;
//   }
//   option {
//     background: ${props => props.theme.modal_background};
//     color: ${props => props.theme.font_color};
//   }
// `;

const ConnectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  border: none;
  background: #10b981;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: #059669;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ConnectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.modal_border};
  background: ${props => props.theme.input_surface_bg || 'transparent'};
`;

const ConnectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConnectionTitle = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.font_color};
  font-family: var(--font-primary);
`;

const ConnectionDetail = styled.span`
  font-size: 0.85rem;
  color: ${props => props.theme.text_secondary || '#666'};
  font-family: var(--font-primary);
`;

const DisconnectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #991b1b;
  background: transparent;
  color: #991b1b;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
  align-self: flex-start;

  &:hover:not(:disabled) {
    background: rgba(153, 27, 27, 0.05);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
